/**
 * One metric, every employee down, every month across.
 *
 * This is the transpose of the grid in the person detail. In the value view a cell is
 * coloured against the same reference as there (review #697, 2026-10-16: "should be
 * off peer average"):
 *
 *   - a scored metric: the scorecard's peer bar for that person and month
 *     (`monthScorer.barFor`) — the bar the cell's points are earned on, so a cell that
 *     earned its points never reads red;
 *   - any other metric: the average of their job title in the same month.
 *
 * Direction-aware, and a deviation under 10% is neutral (`cellTone`).
 */
import { useMemo } from 'react'
import { activeInMonth, type Person } from '@/data/selectors'
import type { TouchMonthRow } from '@/data/types'
import { DataTable, type Column } from './Table'
import { int, pct, dec, money2, monthSpan } from '@/format'
import {
  MONTH_RULES,
  MONTH_MAX,
  MONTH_AVERAGE_WEIGHTING,
  monthAverage,
  type MonthScorer,
} from '@/measures/monthScore'

/** The row average's name and gloss, off the one weighting setting. */
const WEIGHTED = MONTH_AVERAGE_WEIGHTING === 'billed'
const AVG_HEADER = WEIGHTED ? 'Weighted avg' : 'Avg'
const AVG_HOW = WEIGHTED ? 'weighted by billed claims' : 'each month counted once'
import { touchBase, touchedBase } from '@/measures/core'
import { formatPoints, lowVolumeReason } from '@/measures/scorecard'
import type { ScoreKey } from '@/measures/scorecard'
import { COLLECTION_LABEL, collectionWindow, describeWindow } from '@/measures/collection'
import { downloadCSV, downloadXLSX, type ExportColumn } from '@/ui/export'
import { volumeNote, type VolumeFlags } from '@/measures/claimVolume'
import { useState } from 'react'
import { cellTone } from './monthCellTone'

/**
 * A metric that exists at month × employee grain.
 *
 * `group` splits the picker: the measures the scorecard awards points for, and
 * everything else. A reader choosing "month score" and a reader choosing "PTO hours"
 * are asking different questions and should not have to tell them apart by name.
 */
export interface MonthMetric {
  key: string
  label: string
  lowerIsBetter: boolean
  get: (row: TouchMonthRow) => number | null
  format: (v: number | null) => string
  /** Whether the row total is a sum or an average. */
  sum: boolean
  group: 'scorecard' | 'other'
  /** Carries scorecard points, so the grid can offer a points view of it. */
  scored?: boolean
  /**
   * The row total, where it is not the months' sum or mean. Collection's months are
   * overlapping windows, so their mean double-counts; the range's own window is the
   * honest total.
   */
  total?: (p: Person) => number | null
  /** A month column's hover, where each month means something the header does not say. */
  monthTitle?: (month: string) => string
}

const share = (num: number, den: number) => (den === 0 ? null : num / den)

/**
 * Ordered as the approved artifact orders them: the scored measures first, in
 * descending point value, then the rest.
 *
 * "Cost per resolved claim" is deliberately NOT here any more. It was the grid's
 * default, and it is the one cost measure nothing in the app is scored on — a
 * resolved-basis denominator sitting 16% away from the billed one that every other
 * figure uses. Removing it is the point of the change, not a side effect.
 */
export const MONTH_METRICS: MonthMetric[] = [
  {
    key: 'cpcb',
    label: 'Cost per claim',
    lowerIsBetter: true,
    get: (r) => share(r.claimLabor, r.billedClaims),
    format: (v) => money2(v),
    sum: false,
    group: 'scorecard',
    scored: true,
  },
  {
    key: 'billed',
    label: 'Billed claims',
    lowerIsBetter: false,
    get: (r) => r.billedClaims,
    format: (v) => int(v),
    sum: true,
    group: 'scorecard',
    scored: true,
  },
  {
    key: 'rvb',
    label: 'Claims billed vs resolved %',
    lowerIsBetter: false,
    get: (r) => share(r.resolvedClaims, r.billedClaims),
    format: (v) => pct(v),
    sum: false,
    group: 'scorecard',
    scored: true,
  },
  {
    key: 'zeroTouch',
    label: '0% touch share',
    lowerIsBetter: false,
    get: (r) => share(r.resolvedNoTouch, touchBase(r)),
    format: (v) => pct(v),
    sum: false,
    group: 'scorecard',
    scored: true,
  },
  {
    key: 'wk',
    label: 'Open claims worked in month %',
    lowerIsBetter: false,
    get: (r) => share(r.openClaimsWorked, r.openClaims),
    format: (v) => pct(v),
    sum: false,
    group: 'scorecard',
    scored: true,
  },
  {
    key: 'facm',
    label: 'Facilities handled',
    lowerIsBetter: false,
    get: (r) => r.facilities,
    format: (v) => int(v),
    sum: false,
    group: 'scorecard',
    scored: true,
  },
  {
    key: 'labor',
    label: 'Claim labor',
    lowerIsBetter: true,
    get: (r) => r.claimLabor,
    format: (v) => money2(v),
    sum: true,
    group: 'other',
  },
  {
    key: 'resolved',
    label: 'Resolved claims',
    lowerIsBetter: false,
    get: (r) => r.resolvedClaims,
    format: (v) => int(v),
    sum: true,
    group: 'other',
  },
  {
    key: 'openPile',
    label: 'Open claims',
    lowerIsBetter: true,
    get: (r) => r.openClaims,
    format: (v) => int(v),
    sum: false,
    group: 'other',
  },
  {
    key: 'oneTouch',
    label: 'One touch share',
    lowerIsBetter: false,
    get: (r) => share(r.resolvedOneTouch, touchBase(r)),
    format: (v) => pct(v),
    sum: false,
    group: 'other',
  },
  {
    key: 'twoPlus',
    label: 'Two or more touches',
    lowerIsBetter: true,
    get: (r) => share(r.resolvedTwoPlusTouch, touchBase(r)),
    format: (v) => pct(v),
    sum: false,
    group: 'other',
  },
  {
    key: 'touches',
    label: 'Touches per touched claim',
    lowerIsBetter: true,
    get: (r) => share(r.resolvedTouches, touchedBase(r)),
    format: (v) => dec(v),
    sum: false,
    group: 'other',
  },
  {
    key: 'coll',
    label: COLLECTION_LABEL,
    lowerIsBetter: false,
    // The month's own window, bill months M−8 … M−3 — see measures/collection.ts.
    get: (r) => r.collectedPct,
    format: (v) => pct(v),
    sum: false,
    group: 'other',
    total: (p) => p.collectedPct,
    monthTitle: (m) => `${monthSpan([m])} reads ${describeWindow(collectionWindow(m, m))}`,
  },
  {
    key: 'touched',
    label: 'Claims touched',
    lowerIsBetter: false,
    get: (r) => touchedBase(r),
    format: (v) => int(v),
    sum: true,
    group: 'other',
  },
]

/** Which scorecard rule a grid metric carries points for, where one does. */
export const SCORE_KEY_OF: Record<string, ScoreKey | undefined> = {
  cpcb: 'costPerClaim',
  billed: 'claims',
  rvb: 'closedClaimPct',
  zeroTouch: 'zeroTouchShare',
  wk: 'openWorkedPct',
  facm: 'facilities',
}

export const MONTH_METRIC_BY_KEY = new Map(MONTH_METRICS.map((m) => [m.key, m]))

const MONTH_LABEL = (m: string) =>
  new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'short' })

export const exportButton: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 'var(--t-table)',
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid var(--rule-2)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  cursor: 'pointer',
}

/** One person's line in the grid: a cell per month and the row's total or average. */
export interface MonthGridRow {
  person: Person
  cells: (number | null)[]
  total: number | null
}
type Row = MonthGridRow

/**
 * What the cells hold.
 *
 *  - 'value'  — the measure itself, coloured against the cohort that month.
 *  - 'points' — what that measure EARNED that month, 0 or its point value.
 *  - 'score'  — every monthly rule added up, out of MONTH_MAX.
 *
 * Points and score are not decoration on the value view: a reader asking "why is this
 * person at 4.5" needs to see which lines fired, and a reader asking "is 62% good"
 * needs the measure. Switching is one control rather than two tables.
 */
export type GridMode = 'value' | 'points' | 'score'

/**
 * The grid's rows, as on screen: everyone with a month row, one cell per month, and the
 * row's total. Pure, so the Scorecard can export every "Cells show" option at once
 * (review #178) from exactly what each would put on screen.
 */
export function monthGridRows(
  metric: MonthMetric,
  mode: GridMode,
  scorer: MonthScorer | undefined,
  people: readonly Person[],
  months: readonly string[],
): MonthGridRow[] {
  const scoring = mode !== 'value' && scorer !== undefined
  const byMonth = new Map<string, number>(months.map((m, i) => [m, i]))
  const rule = MONTH_RULES.find((r) => SCORE_KEY_OF[metric.key] === r.key)

  return people
    .filter((p) => p.months.length > 0)
    .map((p) => {
      const byIx = new Map<number, (typeof p.months)[number]>()
      for (const row of p.months) {
        const i = byMonth.get(row.month)
        if (i !== undefined) byIx.set(i, row)
      }

      const cells: (number | null)[] = months.map((_, i) => {
        const row = byIx.get(i)
        if (mode === 'score') return scorer ? (scorer.score(p, row, i)?.got ?? null) : null
        if (mode === 'points')
          return scorer && rule ? (scorer.rulePoints(p, rule, row, i)?.pts ?? null) : null
        return row ? metric.get(row) : null
      })

      const present = cells.filter((v): v is number => v !== null)
      // Points and score are a billed-claims WEIGHTED AVERAGE across the months, on
      // the same scale as each month (Shayna, 2026-09-25) — they used to add up, to a
      // figure on no scale anyone scores against. A count measure still totals; a
      // rate still averages, unless it says otherwise (`total`).
      const total = scoring
        ? monthAverage(
            cells,
            months.map((_, i) => byIx.get(i)?.billedClaims ?? 0),
          )
        : metric.total
          ? metric.total(p)
          : present.length
            ? metric.sum
              ? present.reduce((a, b) => a + b, 0)
              : present.reduce((a, b) => a + b, 0) / present.length
            : null
      return { person: p, cells, total }
    })
}

/** The total column's head: what the row's last figure is. */
export function monthGridTotalHeader(metric: MonthMetric, mode: GridMode, scored = true): string {
  return mode !== 'value' && scored
    ? AVG_HEADER
    : metric.total
      ? 'Range'
      : metric.sum
        ? 'Total'
        : 'Average'
}

/**
 * The job title's plain mean per month, over the people active that month — the
 * reference for an unscored metric, and the fallback for a scored one with no peer bar.
 * Somebody who has left is listed, and a peer only for the months they were active
 * (DECISION-LOG §8; review #61, 2026-10-05).
 */
export function titleMonthMeans(
  rows: readonly MonthGridRow[],
  months: readonly string[],
): Map<string, (number | null)[]> {
  const acc = new Map<string, { sum: number; n: number }[]>()
  for (const r of rows) {
    let byTitle = acc.get(r.person.jobTitle)
    if (!byTitle) acc.set(r.person.jobTitle, (byTitle = months.map(() => ({ sum: 0, n: 0 }))))
    r.cells.forEach((v, i) => {
      if (v === null || !activeInMonth(r.person, months[i]!)) return
      byTitle![i]!.sum += v
      byTitle![i]!.n += 1
    })
  }
  const means = new Map<string, (number | null)[]>()
  for (const [title, arr] of acc) {
    means.set(
      title,
      arr.map((a) => (a.n === 0 ? null : a.sum / a.n)),
    )
  }
  return means
}

/**
 * What a value-view cell is coloured against (review #697, 2026-10-16). A scored
 * metric reads against the scorecard's peer bar for that person and month — the bar
 * its points are earned on. Until #697 every metric read against the job title's plain
 * monthly mean, which a few thin months drag a long way (326% against a 71% peer
 * average), so a cell could earn its points and read red. Unscored metrics, and a
 * scored one with no peer bar, keep the title mean.
 */
export function monthGridReference(
  metric: MonthMetric,
  scorer: MonthScorer | undefined,
  person: Person,
  i: number,
  titleMean: number | null,
): { ref: number | null; basis: 'peer' | 'title' } {
  const rule = MONTH_RULES.find((r) => SCORE_KEY_OF[metric.key] === r.key)
  const bar = rule && scorer ? scorer.barFor(person, rule, i) : null
  if (bar !== null && Number.isFinite(bar)) return { ref: bar, basis: 'peer' }
  return { ref: titleMean, basis: 'title' }
}

export function EmployeeMonthGrid({
  metric,
  mode = 'value',
  scorer,
  people,
  months,
  openMonths,
  onOpenPerson,
  exportMore,
  volume,
}: {
  /**
   * The build's claim-volume warning (measures/claimVolume.ts), marked ⚑ on
   * the person and the cell. Worked out over the whole population by the caller.
   */
  volume?: VolumeFlags
  /** More export buttons, after this view's own: the Scorecard's every-option file. */
  exportMore?: React.ReactNode
  metric: MonthMetric
  mode?: GridMode
  /** Required for the 'points' and 'score' modes; ignored otherwise. */
  scorer?: MonthScorer
  people: Person[]
  months: string[]
  openMonths: string[]
  onOpenPerson: (name: string) => void
}) {
  const scoring = mode !== 'value' && scorer !== undefined
  const { rows, cohortAvg } = useMemo(() => {
    const rows = monthGridRows(metric, mode, scorer, people, months)
    return { rows, cohortAvg: titleMonthMeans(rows, months) }
  }, [people, months, metric, mode, scorer])

  const tone = (r: Row, i: number): { bg: string; fg: string } => {
    const v = r.cells[i] ?? null
    // In the points and score views the number IS the verdict, so it is coloured
    // against what was available rather than against the cohort — colouring points
    // against the cohort's points would say "below average at being above average".
    if (scoring) {
      if (v === null) return { bg: 'transparent', fg: 'var(--ink-3)' }
      if (v > 0) return { bg: 'var(--good-bg)', fg: 'var(--good-ink)' }
      return { bg: 'var(--crit-bg)', fg: 'var(--crit-ink)' }
    }
    const titleMean = cohortAvg.get(r.person.jobTitle)?.[i] ?? null
    const { ref } = monthGridReference(metric, scorer, r.person, i, titleMean)
    return cellTone(v, ref, !metric.lowerIsBetter)
  }

  const [saveNote, setSaveNote] = useState<string | null>(null)

  /** One cell's text. Points print with a sign; measures use their own format. */
  const cellText = (v: number | null) =>
    v === null ? metric.format(null) : scoring ? formatPoints(v) : metric.format(v)

  const totalHeader = monthGridTotalHeader(metric, mode, scorer !== undefined)

  /** How many of the row's months on screen the build flagged (the flags cover the whole period). */
  const flaggedIn = (r: Row) => {
    const marks = volume?.byPerson.get(r.person.employee)
    return marks ? months.filter((m) => marks.has(m)).length : 0
  }

  /** Exports carry RAW values — formatting belongs on screen, not in a spreadsheet. */
  const exportColumns: ExportColumn<Row>[] = [
    { header: 'Employee', value: (r) => r.person.employee },
    { header: 'Paylocity ID', value: (r) => r.person.paylocityId ?? '' },
    { header: 'Job title', value: (r) => r.person.jobTitle },
    { header: 'Department', value: (r) => r.person.department },
    ...months.map((m, i) => ({
      header: MONTH_LABEL(m) + (openMonths.includes(m) ? ' *' : ''),
      value: (r: Row) => r.cells[i] ?? null,
    })),
    {
      header: totalHeader,
      value: (r: Row) => r.total,
    },
  ]

  const columns: Column<Row>[] = [
    {
      key: 'name',
      header: 'Employee',
      frozen: true,
      width: 190,
      sort: (r) => r.person.employee,
      render: (r) => {
        const marked = flaggedIn(r)
        return (
          <span
            style={{
              fontSize: 'var(--t-table)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
            }}
            title={
              marked > 0
                ? `${r.person.employee}: ${marked} month${marked === 1 ? '' : 's'} of billed-claim volume far above the job title (${volume?.rule}). Hover a ⚑ month for its figures.`
                : r.person.employee
            }
          >
            {marked > 0 && <span className="pts-chip warn" style={{ marginRight: 5 }}>⚑ volume</span>}
            {r.person.employee}
          </span>
        )
      },
    },
    {
      // Review #373: the key other systems know the person by.
      key: 'pid',
      header: 'Paylocity ID',
      width: 96,
      dim: true,
      sort: (r) => r.person.paylocityId,
      render: (r) => (
        <span className="mono" style={{ fontSize: 'var(--t-rank)' }}>
          {r.person.paylocityId ?? '—'}
        </span>
      ),
    },
    {
      key: 'jt',
      header: 'Job title',
      width: 170,
      dim: true,
      sort: (r) => r.person.jobTitle,
      render: (r) => (
        <span
          style={{
            fontSize: 'var(--t-rank)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
          }}
          title={r.person.jobTitle}
        >
          {r.person.jobTitle}
        </span>
      ),
    },
    ...months.map((m, i) => ({
      key: `m${i}`,
      header: MONTH_LABEL(m) + (openMonths.includes(m) ? ' *' : ''),
      title: !scoring && metric.monthTitle ? metric.monthTitle(m) : undefined,
      align: 'right' as const,
      width: 88,
      sort: (r: Row) => r.cells[i] ?? null,
      render: (r: Row) => {
        const t = tone(r, i)
        const marked = volume?.byPerson.get(r.person.employee)?.get(m)
        // A month scored 0 below the minimum billing volume says so, and what it would have scored (#695).
        const low =
          mode === 'score' && scorer
            ? scorer.score(r.person, r.person.months.find((x) => x.month === m), i)?.lowVolume
            : undefined
        const title = [marked ? volumeNote(marked) : null, low ? lowVolumeReason(low) : null].filter(Boolean).join(' ')
        return (
          <span
            className="mono"
            data-low-volume={low ? '' : undefined}
            title={title || undefined}
            style={{
              fontSize: 'var(--t-table)',
              background: t.bg,
              color: t.fg,
              borderRadius: 4,
              padding: '2px 6px',
              margin: '-2px -6px',
              display: 'inline-block',
              // Marked: an outline, so the green or red still reads underneath.
              outline: marked ? '1.5px dashed var(--warn)' : undefined,
              outlineOffset: marked ? -1 : undefined,
            }}
          >
            {marked && <span style={{ color: 'var(--warn-ink)', marginRight: 3 }}>⚑</span>}
            {cellText(r.cells[i] ?? null)}
            {low && <span style={{ color: 'var(--ink-3)', marginLeft: 3, fontSize: 10 }}>below min.</span>}
          </span>
        )
      },
    })),
    {
      key: 'total',
      header: totalHeader,
      title: scoring
        ? `The months’ figures averaged, ${AVG_HOW}. Not the range score: that is scored on the range’s pooled figures.`
        : metric.total
          ? 'The whole range’s own figure, not an average of the months'
          : undefined,
      align: 'right',
      width: 104,
      divider: true,
      sort: (r) => r.total,
      render: (r) => (
        <span className="mono" style={{ fontSize: 'var(--t-table)' }}>
          {cellText(r.total)}
        </span>
      ),
    },
  ]

  const save = (kind: 'csv' | 'xlsx') => {
    const stem = `month-by-month-${mode === 'score' ? 'score' : metric.key}${
      mode === 'points' ? '-points' : ''
    }`
    const result =
      kind === 'csv'
        ? downloadCSV(`${stem}.csv`, rows, exportColumns)
        : downloadXLSX(`${stem}.xlsx`, rows, exportColumns, 'Month by month')
    setSaveNote(
      result === 'ok'
        ? null
        : 'This view cannot start a download — an embedded preview blocks it.',
    )
  }

  return (
    <>
      <p className="body-note" style={{ marginTop: 0 }}>
        {scoring ? (
          <>
            Points earned that month{mode === 'score' ? ` — out of ${MONTH_MAX}` : ''},
            against the peer average. Blank means no figure to score, not a zero.
          </>
        ) : (
          <>Click a column header to sort; click a row to open that person.</>
        )}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 10 }}>
        <span className="eyebrow">Export</span>
        <button type="button" onClick={() => save('xlsx')} style={exportButton}>
          Excel
        </button>
        <button type="button" onClick={() => save('csv')} style={exportButton}>
          CSV
        </button>
        <span style={{ fontSize: 'var(--t-rank)', color: 'var(--ink-3)' }}>
          {int(rows.length)} rows, exactly what is on screen
        </span>
        {exportMore}
      </div>
      {saveNote && (
        <p
          className="mono"
          style={{ fontSize: 'var(--t-micro)', color: 'var(--crit-ink)', marginTop: 8 }}
        >
          {saveNote}
        </p>
      )}

      <div className="card" style={{ marginTop: 12, padding: 0, overflow: 'hidden' }}>
        <DataTable
          storageKey="employee-month-grid"
          sortParam="grid.sort"
          rows={rows}
          columns={columns}
          rowKey={(r) => r.person.employee}
          onRowClick={(r) => onOpenPerson(r.person.employee)}
          initialSort="total"
          // In a points or score view more is always better, whatever the
          // underlying measure does — a low-is-better metric must not open its
          // points view with the lowest scorers at the top.
          initialDir={!scoring && metric.lowerIsBetter ? 'asc' : 'desc'}
          maxHeight="62vh"
        />
      </div>
      {/* What the colours mean — asked for by Shayna, 2026-09-25. */}
      <p className="grid-key">
        {scoring ? (
          <>
            <span className="key-item">
              <span className="key-sw good" />
              earned points that month
            </span>
            <span className="key-item">
              <span className="key-sw bad" />
              earned none, or the −3 penalty
            </span>
            <span className="key-item">
              <b>{AVG_HEADER}</b>
              {mode === 'score'
                ? `the months’ scores averaged, ${AVG_HOW} — still out of ${MONTH_MAX}, and not the range score`
                : `the months’ points averaged, ${AVG_HOW}`}
            </span>
          </>
        ) : (
          <>
            <span className="key-item">
              <span className="key-sw good" />
              {metric.scored
                ? '10%+ better than the peer average its points are scored on'
                : '10%+ better than the job title’s average that month'}
            </span>
            <span className="key-item">
              <span className="key-sw bad" />
              10%+ worse
            </span>
            <span className="key-item">
              <span className="key-sw" />
              within 10%, or no average to compare
            </span>
          </>
        )}
        {rows.some((r) => flaggedIn(r) > 0) && (
          <span className="key-item" title="Review #374. Usually claims assigned to the person automatically, not work they did. Nothing is changed or left out: the month counts as it is.">
            <span style={{ color: 'var(--warn-ink)' }}>⚑</span>
            billed-claim volume far above the job title ({volume?.rule}): read with care
          </span>
        )}
      </p>
      {months.some((m) => openMonths.includes(m)) && (
        <p
          className="mono"
          style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)', marginTop: 10 }}
        >
          * month not closed at export — the rate is real, the volume is not
        </p>
      )}
    </>
  )
}
